/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useMemo } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { RentabilidadDailyRow } from '../../domain/reportsIntelligenceTypes';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  aggregateRentabilidadDailyForChart,
  chartBucketHint,
  chooseChartBucketMode,
} from '../reportsChartAggregation';
import { formatMoneyCompact } from '../../../shared/format/public-api';

type Props = {
  rows: RentabilidadDailyRow[];
  /** Días del rango seleccionado: decide el bucket (día/semana/mes). Fallback: cantidad de filas. */
  rangeDays?: number;
};

/** Barras de margen diario (o agrupado); altura relativa al máximo |margen| del rango. */
export function ReportsMarginDailyBars({ rows, rangeDays }: Props) {
  const { mode, aggregated } = useMemo(() => {
    const mode = chooseChartBucketMode(rangeDays ?? rows.length);
    return { mode, aggregated: aggregateRentabilidadDailyForChart(rows, mode) };
  }, [rows, rangeDays]);

  if (aggregated.length === 0) {
    return (
      <View accessibilityLabel="Gráfico de margen: sin datos agrupados">
        <Text style={styles.caption} accessibilityRole="text">
          No hay series suficientes para el gráfico.
        </Text>
      </View>
    );
  }
  const maxAbs = Math.max(...aggregated.map((r) => Math.abs(r.margenReposicionTotal)), 1);
  const hint = chartBucketHint(mode);
  const colWidth = mode === 'month' ? 72 : mode === 'week' ? 64 : 56;

  return (
    <View
      style={{ gap: spacing.sm }}
      accessibilityLabel={`Gráfico de margen ${mode === 'day' ? 'por día' : mode === 'week' ? 'por semana' : 'por mes'}. Deslizá horizontalmente si hay muchas columnas. Verde: positivo. Rojo: negativo.`}
    >
      <Text style={styles.reportsSheetBlockTitle}>
        {mode === 'day' ? 'Margen por día' : mode === 'week' ? 'Margen por semana' : 'Margen por mes'}
      </Text>
      {hint ? (
        <Text style={[styles.caption, { color: ticketColors.ink3 }]} accessibilityRole="text">
          {hint}
        </Text>
      ) : null}
      <Text style={[styles.caption, { fontSize: 11, color: ticketColors.ink3 }]}>
        Verdes: margen estimado positivo. Rojos: negativo.
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator
        contentContainerStyle={{ gap: spacing.xs, paddingVertical: spacing.xs, paddingRight: spacing.md }}
      >
        {aggregated.map((r) => {
          const m = r.margenReposicionTotal;
          const barH = Math.max(8, Math.round((Math.abs(m) / maxAbs) * 88));
          const barColor = m >= 0 ? ticketColors.ok : colors.danger;
          return (
            <View key={r.key} style={{ alignItems: 'center', width: colWidth }}>
              <View
                style={{
                  height: 104,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  width: '100%',
                }}
                accessibilityLabel={`${r.label}: ${Math.round(m)} pesos de margen estimado`}
              >
                <View
                  style={{
                    width: '62%',
                    maxWidth: 30,
                    height: barH,
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    backgroundColor: barColor,
                    opacity: 0.9,
                  }}
                />
              </View>
              <Text style={[styles.caption, { marginTop: 4, fontSize: 11 }]} numberOfLines={2}>
                {r.label}
              </Text>
              <Text style={[styles.caption, { fontSize: 10, color: ticketColors.ink2 }]} numberOfLines={1}>
                {formatMoneyCompact(Math.round(m))}
              </Text>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}
